/**
 * The one place a `423 organization_locked` refusal is turned into a visit to
 * the "Your organisation is locked" page. Every axios instance that talks to a
 * customer backend attaches it (`attachOrgLockedHandler(instance)`), so a page
 * that fires five mutating calls at a locked org produces ONE redirect and one
 * /me refresh, not five toasts (`createLockedRedirector` in
 * utils/org-lifecycle.js holds the burst guard).
 *
 * Router and store are imported lazily: this module is loaded by the service
 * files, which the store graph loads before the router exists.
 *
 * Author: Matew Makhatadze
 */
import { createLockedRedirector, isOrganizationLockedError } from '@/utils/org-lifecycle'

let routerRef = null
let storeRef = null

async function deps() {
  if (!routerRef) routerRef = (await import('@/router')).default
  if (!storeRef) storeRef = (await import('@/store')).default
  return { router: routerRef, store: storeRef }
}

const redirectOnce = createLockedRedirector({
  currentPath: () => routerRef?.currentRoute?.value?.path || '',
  refresh: () => {
    deps().then(({ store }) => {
      if (store?.state?.auth?.isAuthenticated) store.dispatch('auth/fetchMe')
    }).catch(() => {})
  },
  async navigate(path) {
    const { router } = await deps()
    if (router.currentRoute.value.path !== path) await router.push(path)
  }
})

/** Mark the error so callers can stay quiet (`err.organizationLocked`). */
export function handleOrgLockedError(error) {
  if (isOrganizationLockedError(error)) {
    try { error.organizationLocked = true } catch { /* frozen error */ }
    redirectOnce()
  }
  return Promise.reject(error)
}

const ATTACHED = new WeakSet()

export function attachOrgLockedHandler(instance) {
  if (!instance || ATTACHED.has(instance)) return instance
  ATTACHED.add(instance)
  instance.interceptors.response.use((r) => r, handleOrgLockedError)
  return instance
}
